<template>
  <div class="cf-flex cf-flex--column cf-flex--gap-12">
    <a
      v-if="session.route.returnUrl"
      :href="session.route.returnUrl"
    >
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--gap-12">
        <span class="cf-icon far fa-chevron-left" />

        <p class="cf-subtitle cf-subtitle--3">
          View All Restaurants
        </p>
      </div>
    </a>

    <div
      title="Change order details"
      role="button"
      :class="['cf-order-address-details', { 'cf-order-address-details--no-click': isReadOnly }]"
      @click="editOrderAddressDetailsHandler"
    >
      <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-center cf-flex--gap-10 cf-flex--no-shrink cf-padding cf-border-right cf-max-width-145">
        <img
          v-if="orderDetailsContent.imageUrl"
          :src="orderDetailsContent.imageUrl"
          :alt="orderDetailsContent.name"
          class="cf-order-mode-image"
        >
        <span
          v-else
          :class="`cf-icon primary far ${ orderDetailsContent.icon }`"
        />

        <div class="cf-flex cf-flex--column">
          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            {{ orderDetailsContent.name }}
          </p>

          <p
            v-if="isTableNumberDisplayed"
            class="cf-subtitle cf-subtitle--3"
          >
            {{ userLocationType.description }}

            {{ tableNumberDisplay }}
          </p>
        </div>
      </div>

      <div
        v-if="selectedLocation.storeId"
        class="cf-flex cf-full-width cf-padding-12"
      >
        <div
          v-if="['delivery', 'shipping'].includes(orderDetails.fulfillmentType)"
          :class="['cf-flex cf-flex--column cf-flex--justify-center', { 'cf-max-width-175': !isReadOnly }, { 'cf-full-width': !isReadOnly }]"
        >
          <p
            v-if="orderDetails.deliveryAddress.type"
            class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-no-wrap-ellipsis-multi-line"
          >
            {{ orderDetails.deliveryAddress.type }}
          </p>

          <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis-multi-line">
            <template v-if="orderDetails.deliveryAddress.line1">
              {{ orderDetails.deliveryAddress.line1 }}
            </template>

            <template v-if="orderDetails.deliveryAddress.line2">
              {{ orderDetails.deliveryAddress.line2 }},
            </template>
          </p>

          <p class="cf-subtitle cf-subtitle--2">
            {{ orderDetails.deliveryAddress.city }},
            {{ orderDetails.deliveryAddress.state }}
          </p>

          <p class="cf-subtitle cf-subtitle--2">
            {{ orderDetails.deliveryAddress.postalCode }}
          </p>
        </div>

        <div
          v-else-if="orderDetails.fulfillmentType === 'room-service'"
          class="cf-flex cf-flex--column cf-flex--grow-1"
        >
          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy">
            {{ selectedLocation.description }}
          </p>

          <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis">
            Deliver to Room
          </p>
        </div>

        <div
          v-else
          :class="['cf-flex cf-flex--column cf-flex--justify-center', { 'cf-max-width-175': !isReadOnly }, { 'cf-full-width': !isReadOnly }]"
        >
          <p class="cf-subtitle cf-subtitle--2 cf-subtitle--heavy cf-no-wrap-ellipsis-multi-line">
            {{ selectedLocation.description }}
          </p>

          <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis-multi-line">
            {{ selectedLocation.address.line1 }}
            {{ selectedLocation.address.line2 }}
          </p>

          <p class="cf-subtitle cf-subtitle--2 cf-no-wrap-ellipsis-multi-line">
            {{ selectedLocation.address.city }},
            {{ selectedLocation.address.state }}
          </p>
        </div>

        <div
          v-if="!isReadOnly"
          class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end cf-flex--grow-1"
        >
          <button
            class="cf-button cf-button--tertiary cf-button--xs"
            type="button"
          >
            Edit
          </button>
        </div>
      </div>

      <div
        v-else-if="!isReadOnly"
        class="cf-grid cf-grid--2-grid-column-left-large cf-grid--align-center cf-full-width cf-padding-12"
      >
        <p class="cf-subtitle cf-subtitle--2">
          Please select a location
        </p>

        <div class="cf-flex cf-flex--row cf-flex--align-center cf-flex--justify-end">
          <button
            class="cf-button cf-button--tertiary cf-button--xs"
            type="button"
          >
            Select
          </button>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
  import { mapGetters } from 'vuex';


  export default {
    name: 'CfOrderAddressDetails',

    computed: {
      ...mapGetters('content', ['content']),
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['fulfillmentEstimateTimestamp', 'order', 'showGroupOrderButton', 'tableNumberDisplay', 'userLocationType']),
      ...mapGetters('session', ['orderDetails', 'session']),

      isReadOnly() {
        return this.orderDetails.fulfillmentType === 'dine-in' || ['group', 'table'].includes(this.orderDetails.orderType);
      },

      isTableNumberDisplayed() {
        return this.tableNumberDisplay && (this.orderDetails.orderType === 'table' || ['dine-in', 'room-service'].includes(this.orderDetails.fulfillmentType));
      },

      orderDetailsContent() {
        const { orderType, fulfillmentType, pickupType } = this.orderDetails;
        const { metadata } = this.content.OrderModes;

        switch (true) {
          case orderType === 'table' || fulfillmentType === 'dine-in':
            return { name: metadata.menuTypeLabelDineIn, icon: 'fa-utensils', imageUrl: metadata.dineInImageUrl };

          case orderType === 'catering' && fulfillmentType === 'delivery':
            return { name: metadata.cateringDeliveryLabel, icon: 'fa-truck', imageUrl: metadata.cateringDeliveryImageUrl };

          case orderType === 'catering':
            return { name: metadata.cateringPickupLabel, icon: 'fa-hat-chef', imageUrl: metadata.cateringPickupImageUrl };

          case fulfillmentType === 'delivery':
            return { name: metadata.menuTypeLabelDelivery, icon: 'fa-car', imageUrl: metadata.deliveryImageUrl };

          case fulfillmentType === 'shipping':
            return { name: metadata.menuTypeLabelShipping, icon: 'fa-truck', imageUrl: metadata.shippingImageUrl };

          case fulfillmentType === 'room-service':
            return { name: metadata.menuTypeLabelRoomService || 'Room service', icon: 'fa-door-closed', imageUrl: metadata.roomServiceImageUrl };

          case pickupType === 'curbside':
            return { name: metadata.menuTypeLabelCurbsidePickup || 'Curbside Pickup', icon: 'fa-car-side', imageUrl: metadata.curbsidePickupImageUrl };

          case pickupType === 'drive-through':
            return { name: metadata.menuTypeLabelDriveThruPickup || 'Drive Thru Pickup', icon: 'fa-car', imageUrl: metadata.driveThruPickupImageUrl };

          default:
            return { name: metadata.menuTypeLabelPickup, icon: 'fa-shopping-bag', imageUrl: metadata.pickupImageUrl };
        }
      }
    },

    methods: {
      editOrderAddressDetailsHandler() {
        if (this.merchant.options.locationChangeEnabled || !this.selectedLocation.storeId) {
          this.$openModal('editOrderDetails');
        }
        else {
          this.$openModal('locationDetails', { location: this.selectedLocation });
        }
      }
    }
  };
</script>
